import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { getGuideReturnTo } from '../lib/guide-navigation';

const chapters = [
  ['register', 'register'], ['login', 'login'], ['watch', 'watch'],
  ['settings', 'settings'], ['withdrawal', 'withdrawal'],
] as const;

type ChapterId = (typeof chapters)[number][0];
type GuideImage = { src: string; alt: string; caption: string; bullets?: string[] };
type Callout = { label: string; frame: string; badge: string };

const imageCallouts: Partial<Record<ChapterId, Record<number, Callout[]>>> = {
  register: {
    0: [{ label: '1', frame: 'left-[36%] top-[53%] h-[6%] w-[28%]', badge: 'left-[32%] top-[51%]' }],
    1: [
      { label: '2', frame: 'left-[35%] top-[17%] h-[13%] w-[40%]', badge: 'left-[31%] top-[14%]' },
      { label: '3', frame: 'left-[35%] top-[37%] h-[12%] w-[40%]', badge: 'left-[31%] top-[35%]' },
      { label: '4', frame: 'left-[35%] top-[56%] h-[6%] w-[40%]', badge: 'left-[31%] top-[54%]' },
      { label: '5', frame: 'left-[25%] top-[69%] h-[5%] w-[50%]', badge: 'left-[21%] top-[67%]' },
      { label: '6', frame: 'left-[23%] top-[76%] h-[6%] w-[54%]', badge: 'left-[19%] top-[74%]' },
    ],
    3: [{ label: '1', frame: 'left-[40%] top-[54%] h-[7%] w-[20%]', badge: 'left-[36%] top-[52%]' }],
  },
  login: {
    0: [{ label: '1', frame: 'left-[84%] top-[6%] h-[4%] w-[8%]', badge: 'left-[81%] top-[3%]' }],
    1: [{ label: '2', frame: 'left-[36%] top-[44%] h-[6%] w-[28%]', badge: 'left-[32%] top-[42%]' }],
  },
  watch: {
    0: [{ label: '1', frame: 'left-[8%] top-[45%] h-[22%] w-[13%]', badge: 'left-[5%] top-[42%]' }],
    1: [
      { label: '2', frame: 'left-[73%] top-[46%] h-[4%] w-[25%]', badge: 'left-[70%] top-[44%]' },
      { label: '3', frame: 'left-[73%] top-[50%] h-[4%] w-[25%]', badge: 'left-[70%] top-[52%]' },
    ],
    2: [
      { label: '4', frame: 'left-[71%] top-[18%] h-[4%] w-[12%]', badge: 'left-[68%] top-[16%]' },
      { label: '5', frame: 'left-[72%] top-[59%] h-[5%] w-[17%]', badge: 'left-[69%] top-[57%]' },
      { label: '6', frame: 'left-[90%] top-[59%] h-[5%] w-[6%]', badge: 'left-[94%] top-[57%]' },
    ],
    3: [
      { label: '7', frame: 'left-[83%] top-[23%] h-[4%] w-[13%]', badge: 'left-[80%] top-[21%]' },
      { label: '8', frame: 'left-[72%] top-[59%] h-[5%] w-[17%]', badge: 'left-[69%] top-[57%]' },
      { label: '9', frame: 'left-[90%] top-[59%] h-[5%] w-[6%]', badge: 'left-[94%] top-[57%]' },
    ],
  },
  settings: {
    0: [{ label: '1', frame: 'left-[95%] top-[6%] h-[4%] w-[4%]', badge: 'left-[91.5%] top-[3%]' }],
    1: [
      { label: '2', frame: 'left-[57%] top-[56%] h-[4%] w-[9%]', badge: 'left-[53.5%] top-[54%]' },
    ],
    2: [
      { label: '3', frame: 'left-[26%] top-[7%] h-[30%] w-[47%]', badge: 'left-[22.5%] top-[5%]' },
      { label: '4', frame: 'left-[26%] top-[39%] h-[24%] w-[47%]', badge: 'left-[22.5%] top-[37%]' },
    ],
  },
  withdrawal: {
    0: [{ label: '1', frame: 'left-[95%] top-[6%] h-[4%] w-[4%]', badge: 'left-[91.5%] top-[3%]' }],
    1: [{ label: '2', frame: 'left-[57%] top-[56%] h-[4%] w-[9%]', badge: 'left-[53.5%] top-[54%]' }],
    2: [{ label: '3', frame: 'left-[26%] top-[65%] h-[13%] w-[47%]', badge: 'left-[22.5%] top-[63%]' }],
    3: [
      { label: '4', frame: 'left-[28%] top-[66.5%] h-[3%] w-[44%]', badge: 'left-[24.5%] top-[64.5%]' },
      { label: '5', frame: 'left-[28%] top-[70%] h-[2%] w-[33%]', badge: 'left-[24.5%] top-[69%]' },
    ],
  },
};

const imageCalloutsEn: Partial<Record<ChapterId, Record<number, Callout[]>>> = {
  register: {
    0: [{ label: '1', frame: 'left-[37%] top-[46.5%] h-[3.5%] w-[27%]', badge: 'left-[33%] top-[44.5%]' }],
    1: [
      { label: '2', frame: 'left-[36%] top-[16%] h-[12%] w-[40%]', badge: 'left-[32%] top-[14%]' },
      { label: '3', frame: 'left-[36%] top-[32%] h-[13%] w-[40%]', badge: 'left-[32%] top-[30%]' },
      { label: '4', frame: 'left-[36%] top-[51%] h-[6%] w-[40%]', badge: 'left-[32%] top-[49%]' },
      { label: '5', frame: 'left-[23%] top-[64%] h-[4%] w-[54%]', badge: 'left-[19%] top-[62%]' },
      { label: '6', frame: 'left-[23%] top-[70%] h-[4%] w-[54%]', badge: 'left-[19%] top-[68%]' },
    ],
    3: [{ label: '1', frame: 'left-[25%] top-[22%] h-[4%] w-[18%]', badge: 'left-[21.5%] top-[20%]' }],
  },
  login: {
    0: [{ label: '1', frame: 'left-[85%] top-[5%] h-[4%] w-[7%]', badge: 'left-[82%] top-[3%]' }],
    1: [{ label: '2', frame: 'left-[37%] top-[39%] h-[4%] w-[27%]', badge: 'left-[33%] top-[37%]' }],
  },
  watch: {
    0: [{ label: '1', frame: 'left-[8%] top-[49%] h-[22%] w-[13%]', badge: 'left-[5%] top-[47%]' }],
    1: [
      { label: '2', frame: 'left-[74%] top-[46%] h-[4%] w-[24%]', badge: 'left-[71%] top-[44%]' },
      { label: '3', frame: 'left-[74%] top-[50%] h-[4%] w-[24%]', badge: 'left-[71%] top-[48%]' },
    ],
    2: [
      { label: '4', frame: 'left-[71%] top-[18%] h-[4%] w-[12%]', badge: 'left-[68%] top-[16%]' },
      { label: '5', frame: 'left-[72%] top-[59%] h-[5%] w-[17%]', badge: 'left-[69%] top-[57%]' },
      { label: '6', frame: 'left-[90%] top-[59%] h-[5%] w-[6%]', badge: 'left-[94%] top-[57%]' },
    ],
    3: [
      { label: '7', frame: 'left-[83%] top-[22%] h-[4%] w-[13%]', badge: 'left-[80%] top-[20%]' },
      { label: '8', frame: 'left-[72%] top-[30%] h-[4%] w-[22%]', badge: 'left-[69%] top-[28%]' },
      { label: '9', frame: 'left-[90%] top-[59%] h-[5%] w-[6%]', badge: 'left-[94%] top-[57%]' },
    ],
  },
  settings: {
    0: [{ label: '1', frame: 'left-[96%] top-[6%] h-[3%] w-[3%]', badge: 'left-[92.5%] top-[4%]' }],
    1: [{ label: '2', frame: 'left-[57%] top-[56.5%] h-[4%] w-[10%]', badge: 'left-[53.5%] top-[54.5%]' }],
    2: [
      { label: '3', frame: 'left-[27%] top-[7%] h-[30%] w-[47%]', badge: 'left-[23.5%] top-[5%]' },
      { label: '4', frame: 'left-[27%] top-[39%] h-[24%] w-[47%]', badge: 'left-[23.5%] top-[37%]' },
    ],
  },
  withdrawal: {
    0: [{ label: '1', frame: 'left-[96%] top-[6%] h-[3%] w-[3%]', badge: 'left-[92.5%] top-[4%]' }],
    1: [{ label: '2', frame: 'left-[57%] top-[56.5%] h-[4%] w-[10%]', badge: 'left-[53.5%] top-[54.5%]' }],
    2: [{ label: '3', frame: 'left-[42%] top-[72%] h-[4%] w-[8%]', badge: 'left-[38.5%] top-[70%]' }],
    3: [
      { label: '4', frame: 'left-[28%] top-[66.5%] h-[3%] w-[44%]', badge: 'left-[24.5%] top-[64.5%]' },
      { label: '5', frame: 'left-[28%] top-[70.5%] h-[3%] w-[44%]', badge: 'left-[24.5%] top-[68.5%]' },
    ],
  },
};

export default function Guide() {
  const { t, i18n } = useTranslation('guide');
  const location = useLocation();
  const returnTo = getGuideReturnTo(location);
  const getImages = (key: string): GuideImage[] => t(`steps.${key}.images`, { returnObjects: true }) as GuideImage[];

  return (
    <main className="min-h-screen bg-gray-50 px-4 py-10 text-gray-900">
      <div className="mx-auto max-w-4xl">
        <div className="mb-3 flex items-center justify-between gap-2">
          <Link className="rounded border border-pink-200 bg-white px-3 py-1.5 text-sm font-semibold text-pink-700 hover:bg-pink-50" to={returnTo}>{t('backToPrevious')}</Link>
          <div className="flex gap-2" aria-label="Language">
          <button type="button" onClick={() => void i18n.changeLanguage('ja')} className="rounded border border-pink-200 bg-white px-3 py-1.5 text-sm font-semibold text-pink-700 hover:bg-pink-50">日本語</button>
          <button type="button" onClick={() => void i18n.changeLanguage('en')} className="rounded border border-pink-200 bg-white px-3 py-1.5 text-sm font-semibold text-pink-700 hover:bg-pink-50">English</button>
          </div>
        </div>
        <header className="rounded-2xl bg-gradient-to-br from-pink-500 to-pink-700 px-6 py-10 text-white shadow-lg sm:px-10">
          <p className="mb-3 text-sm font-semibold tracking-wide">QUEEN&apos;S LIVE</p>
          <h1 className="text-3xl font-bold sm:text-4xl">{t('title')}</h1>
          <p className="mt-4 max-w-2xl text-pink-50">{t('intro')}</p>
        </header>

        <section className="mt-8 rounded-xl bg-white p-6 shadow-sm ring-1 ring-pink-100">
          <h2 className="text-xl font-bold text-gray-900">{t('startTitle')}</h2>
          <ol className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm font-semibold text-pink-900">
            {(t('startSteps', { returnObjects: true }) as string[]).map((step, index, steps) => (
              <li key={step} className="flex items-center gap-3"><span className="rounded-md bg-pink-50 px-4 py-3">{step}</span>{index < steps.length - 1 && <span aria-hidden="true" className="text-pink-500">→</span>}</li>
            ))}
          </ol>
        </section>

        <nav aria-label={t('contents')} className="mt-8 rounded-xl bg-white px-10 py-6 shadow-sm ring-1 ring-gray-200">
          <h2 className="font-bold">{t('contents')}</h2>
          <div className="mt-4 grid gap-2 sm:ml-10 sm:grid-flow-col sm:grid-rows-4 sm:grid-cols-2 sm:gap-x-8">{chapters.map(([id, key], index) => <a key={id} className="flex w-full items-center rounded-md px-6 py-2 text-left text-pink-700 hover:bg-pink-50 hover:underline" href={`#${id}`}><span className="w-8 shrink-0 font-bold">{index + 1}.</span><span>{t(`steps.${key}.title`)}</span></a>)}</div>
        </nav>

        <div className="mt-8 space-y-6">
          {chapters.map(([id, key], chapterIndex) => {
            const images = getImages(key);
            return <section key={id} id={id} className="scroll-mt-6 rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-200 sm:p-8">
              <h2 className="border-l-4 border-pink-600 pl-4 text-left text-2xl font-bold"><span className="mr-3 text-pink-700">{chapterIndex + 1}.</span>{t(`steps.${key}.title`)}</h2>
              <div className="mt-5 space-y-6">{images.map((image, imageIndex) => {
                const callouts = (i18n.language.startsWith('en') ? imageCalloutsEn : imageCallouts)[id]?.[imageIndex] ?? [];
                return <figure key={`${image.src}-${imageIndex}`} className="space-y-3">
                  <figcaption className="rounded-lg border border-pink-200 border-l-4 border-l-pink-600 bg-pink-50 px-10 py-4 text-left text-base font-medium leading-7 text-gray-800">（{imageIndex + 1}）{image.caption}{image.bullets && <ul className="mt-3 ml-5 list-disc space-y-1 pl-6 marker:text-pink-700">{image.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}</ul>}</figcaption>
                  <div className="relative overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                    <img src={image.src} alt={image.alt} aria-label={`guide-image-${id}-${imageIndex + 1}`} className="h-auto w-full" loading="lazy" />
                    {callouts.map((callout) => <span key={callout.label} aria-hidden="true"><span className={`pointer-events-none absolute rounded border-2 border-pink-600 ${callout.frame}`} /><span className={`absolute z-10 flex h-8 w-8 items-center justify-center rounded-full bg-pink-600 text-sm font-bold text-white shadow ${callout.badge}`}>{callout.label}</span></span>)}
                  </div>
                </figure>;
              })}</div>
              {id !== 'watch' && <dl className="mt-6 grid gap-4 text-sm">{(['open', 'action', 'complete', 'note'] as const).map((field) => <div key={field} className={field === 'note' ? 'rounded-lg bg-amber-50 p-4 text-amber-900' : ''}><dt className="font-semibold text-gray-700">{t(field)}</dt><dd className="mt-1 leading-6 text-gray-600">{t(`steps.${key}.${field}`)}</dd></div>)}</dl>}
            </section>;
          })}
        </div>
        <div className="mt-8 text-center"><Link className="inline-flex rounded-lg bg-pink-600 px-5 py-3 font-bold text-white hover:bg-pink-700" to={returnTo}>{t('backToPrevious')}</Link></div>
      </div>
    </main>
  );
}
